import React, { useEffect, useState, useCallback } from "react";
import { Container, Card, Spinner, Alert, Button } from "react-bootstrap";

const SimplePdfViewer = ({ fileUrl ,error,setError}) => {
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  const fetchPdf = useCallback(async () => {
    if (!fileUrl) {
      setError("No file URL provided");
      return;
    }

    setLoading(true);
    setError(null);
    setDownloadProgress(0);

    try {
      const response = await fetch(fileUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentLength = response.headers.get('Content-Length');
      const total = parseInt(contentLength || '0', 10);
      let loaded = 0;

      const reader = response.body.getReader();
      const chunks = [];
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        chunks.push(value);
        loaded += value.length;
        
        if (total > 0) {
          setDownloadProgress(Math.round((loaded / total) * 100));
        }
      }

      const blob = new Blob(chunks, { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      setPdfBlobUrl(blobUrl);
    } catch (err) {
      console.error("Error loading PDF:", err);
      setError(err.message || "Failed to load PDF");
    } finally {
      setLoading(false);
    }
  }, [fileUrl]);

  useEffect(() => {
    fetchPdf();

    // Cleanup blob URL when component unmounts
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [fileUrl, fetchPdf]);

  const handleDownload = () => {
    if (pdfBlobUrl) {
      const link = document.createElement('a');
      link.href = pdfBlobUrl;
      link.download = fileUrl.split('/').pop() || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Container className="mt-3">
      <Card className="p-3">
        {loading && (
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading PDF...</span>
            </Spinner>
            {downloadProgress > 0 && (
              <div className="mt-2">
                <small>Downloading: {downloadProgress}%</small>
              </div>
            )}
          </div>
        )}
        
        {error && (
          <Alert variant="danger">
            {error}
            <div className="mt-2">
              <Button variant="primary" onClick={fetchPdf}>
                Retry
              </Button>
            </div>
          </Alert>
        )}
        
        {pdfBlobUrl && !loading && (
          <>
            <iframe
              src={`${pdfBlobUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              width="100%"
              height="300rem"
              title="PDF Viewer"
              style={{ border: "none" }}
            />
          </>
        )}
      </Card>
    </Container>
  );
};

export default SimplePdfViewer;